import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { CLIENT_ID } from '../../lib/client-context';
import { bookmarkKeys } from './bookmarkKeys';
import {
  BookmarkData,
  FormattedBookmark,
  CreateBookmarkInput,
  UpdateBookmarkInput,
  BookmarkFilters,
  formatBookmark,
} from './bookmarkTypes';
import { applyFilters, sortByCreationDateDesc } from './bookmarkUtils';
import { db } from '../../lib/offline-db';
import { queueOfflineOperation } from '../../lib/offline-sync';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { safeUpdateQueryData } from '../../utils/queryInvalidationUtils';
import { secureApi } from '../../api/secure';

/**
 * Hook for fetching all bookmarks for a specific topic
 * Supports offline mode by using IndexedDB when offline
 */
export function useBookmarks(topicId: string | null, filters?: BookmarkFilters) {
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  // Create a query key that includes any filters
  const queryKey = topicId
    ? filters
      ? [...bookmarkKeys.byTopic(topicId), filters]
      : bookmarkKeys.byTopic(topicId)
    : bookmarkKeys.lists();

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(
        `Starting bookmarks fetch #${fetchId} for topic ${topicId} (online: ${isOnline})`
      );
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching bookmarks from IndexedDB #${fetchId}`);

          try {
            // Get bookmarks from IndexedDB
            const bookmarks = await db.bookmarks
              .where('topic_id')
              .equals(topicId)
              .and(bookmark => !bookmark.isDeleted) // Filter out deleted bookmarks
              .toArray();

            logger.fetch(`Offline mode: loaded ${bookmarks.length} bookmarks from IndexedDB`);

            // Apply filtering and sorting
            let processedBookmarks = bookmarks as BookmarkData[];

            // Apply filters if provided
            if (filters) {
              processedBookmarks = applyFilters(processedBookmarks, filters);
            }

            // Sort bookmarks by date (newest first for consistent sorting)
            const sortedBookmarks = sortByCreationDateDesc(processedBookmarks);

            // Format bookmarks for UI
            return sortedBookmarks.map(bookmark => formatBookmark(bookmark as BookmarkData));
          } catch (offlineError) {
            logger.error(`Error fetching offline bookmarks #${fetchId}:`, offlineError);
            throw new Error('Failed to load bookmarks from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('bookmarks')
          .select('*')
          .eq('topic_id', topicId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Store in IndexedDB for offline access
        if (data && data.length > 0) {
          try {
            await db.bookmarks.bulkPut(data);
            logger.fetch(`Stored ${data.length} bookmarks in IndexedDB`);
          } catch (storageError) {
            logger.error(`Error storing bookmarks in IndexedDB:`, storageError);
            // Continue even if storage fails - this is non-critical
          }
        }

        // Apply filters if provided
        let processedBookmarks = data || [];

        if (filters) {
          processedBookmarks = applyFilters(processedBookmarks, filters);
        }

        // Format bookmarks using the helper function
        const formattedBookmarks = processedBookmarks.map(bookmark =>
          formatBookmark(bookmark as BookmarkData)
        );

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`Bookmarks fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

        return formattedBookmarks;
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'toast',
          context: {
            fetchId,
            operation: 'fetchBookmarks',
            topicId,
            isOnline,
          },
        });

        throw appError;
      }
    },
    enabled: !!topicId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for fetching a single bookmark by ID
 * Supports offline mode by using IndexedDB when offline
 */
export function useBookmark(bookmarkId: string | null) {
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: bookmarkId ? bookmarkKeys.detail(bookmarkId) : bookmarkKeys.details(),
    queryFn: async () => {
      if (!bookmarkId) {
        throw new Error('Bookmark ID is required');
      }

      logger.fetch(`Fetching bookmark ${bookmarkId} (online: ${isOnline})`);

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching bookmark ${bookmarkId} from IndexedDB`);

          try {
            // Get bookmark from IndexedDB
            const bookmark = await db.bookmarks.get(bookmarkId);

            if (!bookmark || bookmark.isDeleted) {
              throw new Error(`Bookmark ${bookmarkId} not found in offline storage`);
            }

            // Format the bookmark
            return formatBookmark(bookmark as BookmarkData);
          } catch (offlineError) {
            logger.error(`Error fetching offline bookmark ${bookmarkId}:`, offlineError);
            throw new Error('Failed to load bookmark from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('bookmarks')
          .select('*')
          .eq('id', bookmarkId)
          .single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.bookmarks.put(data);
        } catch (storageError) {
          logger.error(`Error storing bookmark in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format and return the bookmark
        return formatBookmark(data as BookmarkData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: {
            operation: 'fetchBookmark',
            bookmarkId,
            isOnline,
            fetchId: Date.now().toString().slice(-4), // Add a unique fetch ID for tracing
          },
        });
        throw appError;
      }
    },
    enabled: !!bookmarkId,
  });
}

/**
 * Hook for adding a new bookmark
 * Supports offline mode by storing in IndexedDB and queueing for sync
 */
export function useAddBookmark() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (bookmarkInput: CreateBookmarkInput): Promise<FormattedBookmark> => {
      const { topicId, url } = bookmarkInput;

      // Normalize URL to ensure proper format
      let normalizedUrl = url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }

      logger.store('Adding new bookmark', {
        topicId,
        url: normalizedUrl,
        is_online: isOnline,
      });

      try {
        const supabaseClient = await supabase;
        const {
          data: { user },
        } = await supabaseClient.auth.getUser();

        if (!user) {
          throw new Error('No authenticated user');
        }

        // Create bookmark data object with metadata
        const now = new Date().toISOString();
        const bookmarkId = crypto.randomUUID();

        const bookmarkData: BookmarkData = {
          id: bookmarkId,
          topic_id: topicId,
          user_id: user.id,
          url: normalizedUrl,
          title: null,
          description: null,
          image_url: null,
          summary: null,
          note: null,
          created_at: now,
          updated_at: now,
          metadata: {
            fetched: false,
            processing_status: 'pending',
            client_id: CLIENT_ID,
            last_update: now,
          },
        };

        // If offline, save locally and queue for sync
        if (!isOnline) {
          // Store in IndexedDB
          await db.bookmarks.put(bookmarkData);

          // Queue for sync when online
          await queueOfflineOperation('bookmarks', 'create', bookmarkId, bookmarkData);

          // Return formatted bookmark
          return formatBookmark(bookmarkData);
        }

        // Online flow - send to server
        const { data: newBookmark, error } = await supabaseClient
          .from('bookmarks')
          .insert([bookmarkData])
          .select()
          .single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.bookmarks.put(newBookmark);
          logger.fetch(`Stored bookmark ${newBookmark.id} in IndexedDB`);
        } catch (storageError) {
          logger.error(`Error storing bookmark in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Process the bookmark in the background
        processBookmarkInBackground(bookmarkId, normalizedUrl);

        // Return formatted bookmark
        return formatBookmark(newBookmark);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: {
            operation: 'createBookmark',
            topicId,
            url: normalizedUrl,
            isOnline,
            bookmarkId: bookmarkId || undefined,
          },
        });
        throw appError;
      }
    },

    onSuccess: (newBookmark, variables) => {
      const { topicId } = variables;

      // Update the bookmarks list in the cache by adding the new bookmark
      safeUpdateQueryData<FormattedBookmark[]>(
        queryClient,
        bookmarkKeys.byTopic(topicId),
        oldData => {
          if (!oldData) return [newBookmark];
          return [newBookmark, ...oldData];
        },
        { context: { operation: 'createBookmark', bookmarkId: newBookmark.id, topicId } }
      );

      // Add the new bookmark to the detail cache
      safeUpdateQueryData(queryClient, bookmarkKeys.detail(newBookmark.id), () => newBookmark, {
        context: { operation: 'createBookmark', bookmarkId: newBookmark.id },
      });
    },
  });
}

/**
 * Hook for updating a bookmark
 * Supports offline mode by updating IndexedDB and queueing for sync
 */
export function useUpdateBookmark() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      topicId,
      bookmarkId,
      updates,
    }: {
      topicId: string;
      bookmarkId: string;
      updates: UpdateBookmarkInput;
    }): Promise<FormattedBookmark> => {
      if (!bookmarkId) {
        throw new Error('Bookmark ID is required');
      }

      const now = new Date().toISOString();

      // Create update payload with database field names
      const updatePayload: Partial<BookmarkData> = {
        updated_at: now,
        metadata: {
          client_id: CLIENT_ID,
          last_update: now,
        },
      };

      // Map camelCase properties to snake_case for database
      if (updates.title !== undefined) updatePayload.title = updates.title;
      if (updates.description !== undefined) updatePayload.description = updates.description;
      if (updates.imageUrl !== undefined) updatePayload.image_url = updates.imageUrl;
      if (updates.summary !== undefined) updatePayload.summary = updates.summary;
      if (updates.note !== undefined) updatePayload.note = updates.note;

      // Map metadata properties
      if (updates.metadata) {
        updatePayload.metadata = {
          ...updatePayload.metadata,
          fetched: updates.metadata.fetched !== undefined ? updates.metadata.fetched : undefined,
          error: updates.metadata.error,
          processing_status: updates.metadata.processingStatus,
          ai_summarized: updates.metadata.aiSummarized,
          screenshot_taken: updates.metadata.screenshotTaken,
        };
      }

      logger.store('Updating bookmark', {
        bookmarkId,
        topicId,
        updates: Object.keys(updates),
        is_online: isOnline,
      });

      // If offline, update locally and queue for sync
      if (!isOnline) {
        try {
          // Get current bookmark from IndexedDB
          const currentBookmark = await db.bookmarks.get(bookmarkId);

          if (!currentBookmark || currentBookmark.isDeleted) {
            throw new Error(`Bookmark ${bookmarkId} not found in offline storage`);
          }

          // Merge updates with current data
          const updatedBookmark = {
            ...currentBookmark,
            ...updatePayload,
            metadata: {
              ...currentBookmark.metadata,
              ...updatePayload.metadata,
            },
          };

          // Update in IndexedDB
          await db.bookmarks.put(updatedBookmark);

          // Queue for sync when online
          await queueOfflineOperation('bookmarks', 'update', bookmarkId, updatedBookmark);

          // Return formatted bookmark
          return formatBookmark(updatedBookmark as BookmarkData);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateBookmark', bookmarkId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - update on server
      try {
        const supabaseClient = await supabase;

        // Get current bookmark to access its metadata
        const { data: currentBookmark, error: fetchError } = await supabaseClient
          .from('bookmarks')
          .select('metadata')
          .eq('id', bookmarkId)
          .single();

        if (fetchError) throw fetchError;

        // Preserve existing metadata fields
        if (currentBookmark?.metadata) {
          updatePayload.metadata = {
            ...currentBookmark.metadata,
            ...updatePayload.metadata,
          };
        }

        // Update the bookmark
        const { data, error } = await supabaseClient
          .from('bookmarks')
          .update(updatePayload)
          .eq('id', bookmarkId)
          .select()
          .single();

        if (error) throw error;

        // Update in IndexedDB for offline access
        try {
          // Get current data first to preserve fields not included in the update
          const existingBookmark = await db.bookmarks.get(bookmarkId);

          if (existingBookmark) {
            await db.bookmarks.put({
              ...existingBookmark,
              ...data,
            });
            logger.fetch(`Updated bookmark ${bookmarkId} in IndexedDB`);
          }
        } catch (storageError) {
          logger.error(`Error updating bookmark in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format and return the bookmark
        return formatBookmark(data as BookmarkData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: {
            operation: 'updateBookmark',
            bookmarkId,
            topicId,
            isOnline,
            updateFields: Object.keys(updates),
            timestamp: now,
          },
        });
        throw appError;
      }
    },

    onSuccess: (updatedBookmark, variables) => {
      const { topicId, bookmarkId } = variables;

      // Update bookmark in cache
      safeUpdateQueryData(queryClient, bookmarkKeys.detail(bookmarkId), () => updatedBookmark, {
        context: { operation: 'updateBookmark', bookmarkId },
      });

      // Update bookmark in topic list
      safeUpdateQueryData<FormattedBookmark[]>(
        queryClient,
        bookmarkKeys.byTopic(topicId),
        bookmarks => {
          if (!bookmarks) return undefined;

          return bookmarks.map(bookmark =>
            bookmark.id === bookmarkId ? updatedBookmark : bookmark
          );
        },
        { context: { operation: 'updateBookmark', bookmarkId } }
      );
    },
  });
}

/**
 * Hook for deleting a bookmark
 * Supports offline mode by marking as deleted in IndexedDB and queueing for sync
 */
export function useDeleteBookmark() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ topicId, bookmarkId }: { topicId: string; bookmarkId: string }) => {
      try {
        logger.store('Deleting bookmark', {
          bookmarkId,
          topicId,
          is_online: isOnline,
        });

        // If offline, mark as deleted locally and queue for sync
        if (!isOnline) {
          logger.store('Offline mode: Marking bookmark as deleted locally', { bookmarkId });

          try {
            // Mark as deleted in IndexedDB
            await db.bookmarks.update(bookmarkId, { isDeleted: true });

            // Queue for sync when online
            await queueOfflineOperation('bookmarks', 'delete', bookmarkId, { id: bookmarkId });

            return { bookmarkId, topicId };
          } catch (offlineError) {
            const error = handleError(offlineError, {
              action: 'toast',
              context: { operation: 'deleteBookmark', bookmarkId, mode: 'offline' },
            });
            throw error;
          }
        }

        // Online flow - delete from server
        const { error } = await supabase.from('bookmarks').delete().eq('id', bookmarkId);

        if (error) throw error;

        // Remove from IndexedDB
        try {
          await db.bookmarks.delete(bookmarkId);
          logger.fetch(`Deleted bookmark ${bookmarkId} from IndexedDB`);
        } catch (storageError) {
          logger.error(`Error deleting bookmark from IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return { bookmarkId, topicId };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: {
            operation: 'deleteBookmark',
            bookmarkId,
            topicId,
            isOnline,
            timestamp: Date.now(),
          },
        });
        throw appError;
      }
    },

    onSuccess: result => {
      const { bookmarkId, topicId } = result;

      // Remove bookmark from cache
      queryClient.removeQueries({
        queryKey: bookmarkKeys.detail(bookmarkId),
        exact: true,
      });

      // Update the bookmarks list in the cache by filtering out the deleted bookmark
      safeUpdateQueryData<FormattedBookmark[]>(
        queryClient,
        bookmarkKeys.byTopic(topicId),
        oldData => {
          if (!oldData) return oldData;
          return oldData.filter(bookmark => bookmark.id !== bookmarkId);
        },
        { context: { operation: 'deleteBookmark', bookmarkId, topicId } }
      );
    },
  });
}

/**
 * These bookmark note operations are now deprecated.
 * Use updateBookmark directly with { note: value } in the updates object.
 * The BookmarkNote component has been refactored to use updateBookmark directly.
 */

/**
 * Hook for adding or updating a note to a bookmark
 */
export function useAddBookmarkNote() {
  const updateBookmark = useUpdateBookmark();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      topicId,
      bookmarkId,
      note,
    }: {
      topicId: string;
      bookmarkId: string;
      note: string;
    }) => {
      return updateBookmark.mutateAsync({
        topicId,
        bookmarkId,
        updates: { note },
      });
    },
    onSuccess: (updatedBookmark, variables) => {
      const { bookmarkId, topicId } = variables;

      // Update bookmark in detail cache
      safeUpdateQueryData(queryClient, bookmarkKeys.detail(bookmarkId), () => updatedBookmark, {
        context: { operation: 'updateBookmarkNote', bookmarkId },
      });

      // Update bookmark in topic list cache
      safeUpdateQueryData<FormattedBookmark[]>(
        queryClient,
        bookmarkKeys.byTopic(topicId),
        bookmarks => {
          if (!bookmarks) return undefined;
          return bookmarks.map(bookmark =>
            bookmark.id === bookmarkId ? updatedBookmark : bookmark
          );
        },
        { context: { operation: 'updateBookmarkNote', bookmarkId } }
      );
    },
  });
}

/**
 * Hook for updating a bookmark note
 */
export function useUpdateBookmarkNote() {
  // Reuse the same implementation as adding a note
  return useAddBookmarkNote();
}

/**
 * Hook for deleting a bookmark note
 */
export function useDeleteBookmarkNote() {
  const updateBookmark = useUpdateBookmark();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ topicId, bookmarkId }: { topicId: string; bookmarkId: string }) => {
      return updateBookmark.mutateAsync({
        topicId,
        bookmarkId,
        updates: { note: null },
      });
    },
    onSuccess: (updatedBookmark, variables) => {
      const { bookmarkId, topicId } = variables;

      // Update bookmark in detail cache
      safeUpdateQueryData(queryClient, bookmarkKeys.detail(bookmarkId), () => updatedBookmark, {
        context: { operation: 'deleteBookmarkNote', bookmarkId },
      });

      // Update bookmark in topic list cache
      safeUpdateQueryData<FormattedBookmark[]>(
        queryClient,
        bookmarkKeys.byTopic(topicId),
        bookmarks => {
          if (!bookmarks) return undefined;
          return bookmarks.map(bookmark =>
            bookmark.id === bookmarkId ? updatedBookmark : bookmark
          );
        },
        { context: { operation: 'deleteBookmarkNote', bookmarkId } }
      );
    },
  });
}

/**
 * Hook for getting filtered bookmarks based on search and user filters
 */
export function useFilteredBookmarks(
  topicId: string | null,
  searchQuery?: string,
  userIds?: string[],
  options?: {
    domains?: string[];
    dateRange?: { from?: string; to?: string };
    processingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  }
) {
  // Only include filters if they have values
  const filters: BookmarkFilters = {};
  if (searchQuery?.trim()) filters.searchQuery = searchQuery.trim();
  if (userIds?.length) filters.userIds = userIds;

  // Add additional filters if provided
  if (options?.domains?.length) filters.domains = options.domains;
  if (options?.dateRange) filters.dateRange = options.dateRange;
  if (options?.processingStatus) filters.processingStatus = options.processingStatus;

  // Use the main useBookmarks hook with filters
  const hasActiveFilters = Object.keys(filters).length > 0;

  return useBookmarks(topicId, hasActiveFilters ? filters : undefined);
}

/**
 * Helper function to process bookmark metadata and fetch additional info
 */
async function processBookmarkInBackground(bookmarkId: string, url: string) {
  try {
    // Call the secure API to fetch website metadata, generate a summary, and take a screenshot
    const processedData = await secureApi.supabaseAdmin('processBookmark', {
      bookmarkId,
      url,
    });

    if (!processedData) {
      logger.error('Failed to process bookmark:', { bookmarkId, url });
      return;
    }

    // The update to the bookmark will be handled by the realtime subscription
    // We don't need to manually update the store here
  } catch (error) {
    logger.error('Error processing bookmark in background:', error);

    // Update the bookmark to indicate the error
    try {
      const supabaseClient = await supabase;
      await supabaseClient
        .from('bookmarks')
        .update({
          metadata: {
            fetched: true,
            processing_status: 'failed',
            error: error instanceof Error ? error.message : 'Failed to process bookmark',
            client_id: CLIENT_ID,
          },
        })
        .eq('id', bookmarkId);
    } catch (updateError) {
      logger.error('Error updating bookmark with error status:', updateError);
    }
  }
}
